import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Loader2, Plus, Save, Trash2, X, Pencil, RefreshCw, Building2 } from "lucide-react";
import api from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { useAuth } from "@/contexts/AuthContext";

const inputCls = "h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-zinc-50 focus:border-[#00E575] outline-none text-[13px] font-medium";
const labelCls = "block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold";

const emptyDraft = () => ({
  name: "", type: "internal", member_user_ids: [],
  team_lead_user_id: "", contact_email: "",
  default_strategy: "round_robin", active: true, tenant_id: "",
});

export default function AdminSalesTeams() {
  const { user: currentUser } = useAuth();
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tenantOptions, setTenantOptions] = useState([]);

  // Super-Admin in Global Mode → braucht inline Tenant-Auswahl
  const isSuperAdmin = !!currentUser?.is_super_admin;
  const actingTenantId = (typeof window !== "undefined")
    ? localStorage.getItem("novatus_acting_tenant_id") || ""
    : "";
  const needsTenantPick = isSuperAdmin && !actingTenantId;

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: t }, { data: u }] = await Promise.all([
        api.get("/admin/sales-teams?show_inactive=true"),
        api.get("/users"),
      ]);
      setTeams(t || []);
      setUsers((u || []).filter(x => x.role !== "installer"));
    } catch (e) {
      // In Global-Mode der Super-Admin bekommt 400 ohne Tenant — kein harter Toast.
      if (needsTenantPick && e.response?.status === 400) {
        setTeams([]);
      } else {
        toast.error(e.response?.data?.detail || "Laden fehlgeschlagen");
      }
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  // Tenant-Liste nur einmal laden, wenn Super-Admin in Global Mode ist
  useEffect(() => {
    if (!needsTenantPick) return;
    api.get("/superadmin/tenants")
      .then((r) => setTenantOptions((r.data || []).filter((t) => t.active !== false)))
      .catch(() => setTenantOptions([]));
  }, [needsTenantPick]);

  const openNew = () => setEditor({ isNew: true, ...emptyDraft() });
  const openEdit = (t) => setEditor({
    isNew: false, id: t.id, name: t.name, type: t.type,
    member_user_ids: t.member_user_ids || [],
    team_lead_user_id: t.team_lead_user_id || "",
    contact_email: t.contact_email || "",
    default_strategy: t.default_strategy || "round_robin",
    active: t.active !== false,
    tenant_id: t.tenant_id || "",
  });

  const toggleMember = (uid) => {
    if (!editor) return;
    const m = editor.member_user_ids.includes(uid)
      ? editor.member_user_ids.filter(x => x !== uid)
      : [...editor.member_user_ids, uid];
    setEditor({ ...editor, member_user_ids: m });
  };

  const save = async () => {
    if (!editor.name) { toast.error("Name ist Pflicht"); return; }
    if (needsTenantPick && editor.isNew && !editor.tenant_id) {
      toast.error("Bitte einen Mandanten auswählen (Sie sind im Global-Modus).");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...editor };
      delete payload.isNew; delete payload.id;
      if (!payload.team_lead_user_id) payload.team_lead_user_id = null;
      if (!payload.contact_email) payload.contact_email = null;
      // Nur im Global-Modus tenant_id mitsenden; sonst Backend-Side-Resolution greifen lassen
      if (!needsTenantPick || !editor.isNew) delete payload.tenant_id;
      if (editor.isNew) await api.post("/admin/sales-teams", payload);
      else await api.patch(`/admin/sales-teams/${editor.id}`, payload);
      toast.success(editor.isNew ? "Team angelegt" : "Gespeichert");
      setEditor(null); await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Team "${t.name}" deaktivieren?`)) return;
    await api.delete(`/admin/sales-teams/${t.id}`);
    toast.success("Deaktiviert");
    await load();
  };

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="admin-sales-teams">
      <AppHeader title="Sales-Teams" back="/dashboard" />
      <main className="max-w-5xl mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-3">
            <Users className="text-[#00E575]" size={22} />
            <div>
              <h1 className="font-display text-xl sm:text-2xl font-bold">Sales-Teams</h1>
              <p className="text-[12px] text-zinc-300 font-medium">Interne Vertriebler-Pools (für Auktionen) und externe Partner (feste Zuweisung)</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} data-testid="teams-reload" className="h-9 px-3 rounded-lg bg-[#18181B] border border-zinc-800 hover:border-[#00E575]/40 text-[12px] inline-flex items-center gap-1.5 font-medium"><RefreshCw size={13}/>Neu laden</button>
            <button onClick={openNew} data-testid="teams-new" className="h-9 px-3 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[12px] inline-flex items-center gap-1.5 hover:bg-[#00C964]"><Plus size={14}/>Neues Team</button>
          </div>
        </div>

        {loading && (<div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-8 flex justify-center"><Loader2 className="animate-spin text-zinc-400"/></div>)}

        {!loading && teams.length === 0 && (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-8 text-center text-zinc-300 text-sm font-medium">Noch keine Teams. Klick auf <b className="text-[#00E575]">Neues Team</b>.</div>
        )}

        {!loading && teams.map((t) => (
          <section key={t.id} className={`bg-[#18181B] border rounded-2xl p-4 ${t.active === false ? "border-zinc-800 opacity-60" : "border-zinc-800"}`} data-testid={`team-row-${t.id}`}>
            <div className="flex items-start justify-between gap-3 mb-2">
              <div>
                <h2 className="font-bold text-[15px]">{t.name}</h2>
                <div className="flex gap-2 mt-1">
                  <span className={`text-[12px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${t.type === "internal" ? "bg-[#00E575]/10 text-[#00E575]" : "bg-amber-500/10 text-amber-400"}`}>{t.type === "internal" ? "intern" : "extern"}</span>
                  <span className="text-[12px] font-bold uppercase tracking-wider bg-zinc-800 text-zinc-300 px-2 py-0.5 rounded">{t.member_count || 0} Mitglieder</span>
                  <span className="text-[12px] text-zinc-300">Strategie: {t.default_strategy}</span>
                </div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => openEdit(t)} data-testid={`team-edit-${t.id}`} className="h-8 w-8 rounded bg-[#09090B] border border-zinc-700 hover:border-[#00E575]/50 inline-flex items-center justify-center"><Pencil size={12}/></button>
                {t.active !== false && <button onClick={() => remove(t)} data-testid={`team-del-${t.id}`} className="h-8 w-8 rounded bg-[#09090B] border border-zinc-700 hover:border-red-500/50 hover:text-red-400 inline-flex items-center justify-center"><Trash2 size={12}/></button>}
              </div>
            </div>
            {t.members && t.members.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {t.members.map(m => m && <span key={m.id} className="text-[10.5px] bg-zinc-800 px-2 py-0.5 rounded text-zinc-300">{m.first_name} {m.last_name}</span>)}
              </div>
            )}
          </section>
        ))}
      </main>

      {editor && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center p-2 sm:p-4 overflow-y-auto" onClick={() => !saving && setEditor(null)}>
          <div onClick={e => e.stopPropagation()} className="bg-[#18181B] border border-zinc-700 rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between sticky top-0 bg-[#18181B] border-b border-zinc-800 px-5 py-4 z-10">
              <h2 className="font-display text-lg font-bold">{editor.isNew ? "Neues Team" : "Team bearbeiten"}</h2>
              <button onClick={() => setEditor(null)} className="h-8 w-8 rounded bg-zinc-800 hover:bg-zinc-700"><X size={14}/></button>
            </div>
            <div className="p-5 space-y-4">
              {needsTenantPick && editor.isNew && (
                <div data-testid="team-tenant-wrapper" className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3">
                  <label className="text-[12px] uppercase tracking-widest text-amber-300 mb-1 font-bold flex items-center gap-1.5">
                    <Building2 size={12} /> Mandant *
                  </label>
                  <select
                    value={editor.tenant_id}
                    onChange={(e) => setEditor({ ...editor, tenant_id: e.target.value })}
                    data-testid="team-edit-tenant"
                    className="h-10 w-full bg-[#09090B] border border-amber-500/40 rounded-lg px-3 text-zinc-50 focus:border-amber-400 outline-none text-[13px]"
                  >
                    <option value="">— Mandant auswählen —</option>
                    {tenantOptions.map((t) => (
                      <option key={t.id} value={t.id}>{t.name} ({t.slug})</option>
                    ))}
                  </select>
                  <p className="text-[10.5px] text-amber-200/80 mt-1 leading-snug">
                    Sie sind im <strong>Global-Modus</strong>. Wählen Sie den Mandanten, für den dieses Team gelten soll.
                  </p>
                </div>
              )}
              <div>
                <label className={labelCls}>Name *</label>
                <input value={editor.name} onChange={e => setEditor({ ...editor, name: e.target.value })} data-testid="team-edit-name" className={inputCls} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Typ *</label>
                  <select value={editor.type} onChange={e => setEditor({ ...editor, type: e.target.value })} data-testid="team-edit-type" className={inputCls}>
                    <option value="internal">Intern (darf bieten)</option>
                    <option value="external">Extern (feste Zuweisung)</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Default-Strategie</label>
                  <select value={editor.default_strategy} onChange={e => setEditor({ ...editor, default_strategy: e.target.value })} className={inputCls}>
                    <option value="round_robin">Round-Robin (rotierend)</option>
                    <option value="first_available">First Available</option>
                    <option value="least_loaded">Least Loaded (14 Tage)</option>
                    <option value="team_inbox">Team-Inbox (Team-Lead)</option>
                  </select>
                </div>
              </div>
              {editor.type === "external" && (
                <div>
                  <label className={labelCls}>Sammel-E-Mail (extern)</label>
                  <input type="email" value={editor.contact_email} onChange={e => setEditor({ ...editor, contact_email: e.target.value })} className={inputCls} placeholder="leads@partner-firma.de" />
                </div>
              )}
              <div>
                <label className={labelCls}>Team-Lead (User-ID, optional)</label>
                <select value={editor.team_lead_user_id} onChange={e => setEditor({ ...editor, team_lead_user_id: e.target.value })} className={inputCls}>
                  <option value="">— kein Team-Lead —</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.role})</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Mitglieder</label>
                <div className="max-h-56 overflow-y-auto bg-[#09090B] border border-zinc-700 rounded-lg p-2 space-y-1">
                  {users.map(u => (
                    <label key={u.id} className="flex items-center gap-2 px-2 py-1 hover:bg-zinc-800 rounded cursor-pointer text-[12px] font-medium">
                      <input type="checkbox" checked={editor.member_user_ids.includes(u.id)} onChange={() => toggleMember(u.id)} data-testid={`team-member-${u.id}`} className="accent-[#00E575]"/>
                      <span className="flex-1">{u.first_name} {u.last_name}</span>
                      <span className="text-[12px] text-zinc-300 uppercase">{u.role}</span>
                    </label>
                  ))}
                </div>
              </div>
              <label className="inline-flex items-center gap-2 text-[13px]">
                <input type="checkbox" checked={editor.active} onChange={e => setEditor({ ...editor, active: e.target.checked })} className="accent-[#00E575]"/> Team aktiv
              </label>
            </div>
            <div className="sticky bottom-0 bg-[#18181B] border-t border-zinc-800 px-5 py-3 flex justify-end gap-2">
              <button onClick={() => setEditor(null)} className="h-9 px-4 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-[12px] font-medium">Abbrechen</button>
              <button onClick={save} disabled={saving} data-testid="team-save" className="h-9 px-4 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[12px] inline-flex items-center gap-1.5 hover:bg-[#00C964] disabled:opacity-50">{saving ? <Loader2 size={13} className="animate-spin"/> : <Save size={13}/>}{editor.isNew ? "Anlegen" : "Speichern"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
